import AsyncButton from "@components/button/AsyncButton.component";
import Dropdown from "@components/form/Dropdown.component";
import Danger from "@components/statusIndicators/Danger.component";
import InfoAlert from "@components/statusIndicators/InfoAlert.component";
import SuccessAlert from "@components/statusIndicators/SuccessAlert.component";
import Warning from "@components/statusIndicators/Warning.component";
import { Pane, PaneContent } from "@pages/PageStructure.component";
import BackgroundService from "@services/bg.service";
import ConfigService, { Config} from "@services/config/config.service";
import { getErrorMessage } from "@services/errors.service";
import TenantConfigService from "@services/tenantConfig.service";
import produce from "immer";
import { useCallback, useMemo, useState } from "react";
import { Collapse, Form, Modal, Offcanvas } from "react-bootstrap";
import { atomOneLight, CopyBlock } from "react-code-blocks";
import styled from "styled-components";

const HelpStyles = styled.div`
img {
    max-width: 100%;
}
`;

export interface SnowflakeDetails {
    account_identifier: string;
    host?: string;
    username: string;
    private_key: string;
    private_key_passphrase: string;
    database: string;
    query_warehouse: string;
    loading_warehouse: string;
    build_warehouse: string;
    role: string;
    loader_username?: string;
    loader_private_key?: string;
    loader_private_key_passphrase?: string;
    loader_database?: string;
    loader_role?: string;
}

interface SnowflakeFormProps {
    snowflakeDetails: SnowflakeDetails;
    credsAreValid: boolean;
    tenantConfigId: string;
    showPliableHostedSnowflakeCode: boolean;
    onChange: (attr: keyof SnowflakeDetails, value: any) => any;
    onCredentialsAreValid: (areValid: boolean) => any;
    step: 'CREDS'|'SCRIPT'|'VALIDATE';
}

const SnowflakeSetupForm = ({ snowflakeDetails, onChange, tenantConfigId, onCredentialsAreValid, showPliableHostedSnowflakeCode, credsAreValid, step }: SnowflakeFormProps) => {

    const formIsValid = useMemo(() => {
        return !!snowflakeDetails.account_identifier && !!snowflakeDetails.username && !!snowflakeDetails.database && !!snowflakeDetails.role && !!snowflakeDetails.query_warehouse;
    }, [snowflakeDetails]);

    const [savingConfig, setSavingConfig] = useState(false);
    const [saveConfigError, setSaveConfigError] = useState('');
    const [generatingScript, setGeneratingScript] = useState(false);
    const [generatingScriptError, setGeneratingScriptError] = useState('');
    const [setupSql, setSetupSql] = useState('');
    const [showScriptModal, setShowScriptModal] = useState(false);
    const [activationCode, setActivationCode] = useState('');
    const [creatingHostedSnowflake, setCreatingHostedSnowflake] = useState(false);

    const [testingCredentials, setTestingCredentials] = useState(false);
    const [credentialsError, setCredentialsError] = useState('');
    const [credentialsWarning, setCredentialsWarning] = useState('');

    const generateScript = useCallback(async () => {
        setGeneratingScript(true);

        try {
            const { snowflake_setup_sql } : any = await TenantConfigService.getInstance().showSetupScript(tenantConfigId);
            setSetupSql(snowflake_setup_sql);
            setShowScriptModal(true);
        } catch (err) {
            setGeneratingScriptError(getErrorMessage(err));
        } finally {
            setGeneratingScript(false);
        }
        
    }, [snowflakeDetails, tenantConfigId]);

    const saveSnowflakeDetails = useCallback(async (doGenerateScript: boolean = false) => {
        setSavingConfig(true);

        try {
            // Replace the account_identifier with the host
            const details = produce(snowflakeDetails, draft => {
                draft.host = `https://${draft.account_identifier}.snowflakecomputing.com`;
            });
            await TenantConfigService.getInstance().saveSnowflakeDetails(tenantConfigId, details);
            if (doGenerateScript) {
                await generateScript();
            }
        } catch (err) {
            setSaveConfigError(getErrorMessage(err));
        } finally {
            setSavingConfig(false);
        }
        
    }, [snowflakeDetails, tenantConfigId, generateScript]);

    const createPliableHostedSnowflake = useCallback(async () => {
        setCreatingHostedSnowflake(true);

        try {
            await TenantConfigService.getInstance().createHostedSnowflake(tenantConfigId, activationCode);
            ConfigService.getInstance().loadConfig(); // need to flush tenant config
            onCredentialsAreValid(true);
        } catch (err) {
            setSaveConfigError(getErrorMessage(err));
        } finally {
            setCreatingHostedSnowflake(false);
        }
    }, [activationCode, tenantConfigId]);


    const [useDBT, setUseDBT] = useState<boolean|undefined>(undefined);
    
    const testCredentials = useCallback(async () => {
        // Save credentials here in case they are not using the setup script
        setTestingCredentials(true);

        onCredentialsAreValid(false);
        setCredentialsError('');
        try {
            await saveSnowflakeDetails(false);
            const { has_db_access } : any = await TenantConfigService.getInstance().verifySnowflakeAccess(tenantConfigId);

            if (!has_db_access) {
                throw new Error('Unable to connect to Snowflake.');
            } 
            const {job_id} : any = await TenantConfigService.getInstance().initInfrustructure(tenantConfigId);
            const {infra} : Config = await BackgroundService.getInstance().waitForJob(job_id);


            if (infra.is_ready) {
                onCredentialsAreValid(true);
                if (!infra.snowpipe.setup_complete) {
                    setCredentialsWarning('Your Snowflake account is connected, but we were unable to set up the infrastructure for file uploads. You may need to grant the Pliable user the necessary permissions to create stages and pipes. Error: ' + infra.snowpipe.setup_error);
                }
            } else {
                throw new Error('Error setting up infrastructure.');
            }
        } catch (err) {
            setCredentialsError(getErrorMessage(err));
            onCredentialsAreValid(false);
        } finally {
            setTestingCredentials(false);
        }
    }, [snowflakeDetails, tenantConfigId, saveSnowflakeDetails]);
    const [showAll, setShowAll] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    return <div style={{textAlign: 'left'}}>
        <Modal show={showScriptModal} size="xl" onHide={() => setShowScriptModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Setup Script</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <InfoAlert>This script will create all of the resources (warehouses, databases, users, roles, etc) that Pliable needs to operate in your Snowflake account.</InfoAlert>
                <p>
                    Log into your Snowflake account and run the following script in a new worksheet. Make sure "All Queries" is checked. When you're done, close this window and click on "Validate Connection".
                </p>
                <div style={{maxHeight: 400, overflowX: 'scroll'}} className="mb-3">
                    <CopyBlock
                        text={setupSql}
                        language="sql"
                        showLineNumbers={false}
                        theme={atomOneLight}
                        codeBlock={true}
                    />
                </div>
            </Modal.Body>
        </Modal>
        <Offcanvas show={showHelp} onHide={() => setShowHelp(false)} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Help</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Pane>
                    <PaneContent>
                        <HelpStyles className="p-3">
                            <p>To get your Snowflake host name:</p>
                            <h4>1. Open the account menu at the bottom left-hand side of the screen</h4>
                            {/* <img src={SnowflakeMenuScreen} className="mb-3"/> */}
                            <h4>2. Hover over "Account", and then hover over the currently active account in the pop-out menu.</h4>
                            <p>You may have to scroll down if you have several Snowflake accounts.</p>
                            {/* <img src={SnowflakeMenuScreen2} className="mb-3"/> */}
                            <h4>3. Click on the "Copy Account URL" button and paste it into Pliable</h4>
                            {/* <img src={SnowflakeMenuScreen3} className="mb-3"/> */}
                            
                        </HelpStyles>
                    </PaneContent>
                </Pane>
                
            </Offcanvas.Body>
        </Offcanvas>

        

        {step == 'CREDS' && <>
            <h3 className="mb-0">Snowflake Account Details</h3>
            <p className="text-muted">We'll use these details to generate a script for you to run on your Snowflake account, which will create all of the resources Pliable needs to operate. The only required field is the <strong>host</strong>.</p>

            
            <Form.Group className="mb-3">
                <Form.Label>Account Identifier *</Form.Label>
                <Form.Control
                    name="host"
                    type="text"
                    placeholder="your-account.snowflakecomputing.com"
                    value={snowflakeDetails.account_identifier}
                    isValid={!!snowflakeDetails.account_identifier} 

                    onChange={(e) => onChange('account_identifier', e.target.value)}
                />
                <Form.Text>
                    <a role="button" href="#" onClick={() => setShowHelp(true)}>Where do I find this?</a>
                </Form.Text>
            </Form.Group>
            <div className="card">
                <div className="card-body">
                    <div
                        role="button"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            marginBottom: '0.5rem'
                        }}
                        onClick={() => setShowAll(!showAll)}
                        tabIndex={0}
                        onKeyPress={e => {
                            if (e.key === 'Enter' || e.key === ' ') setShowAll(!showAll);
                        }}
                        aria-expanded={showAll}
                    >
                        <i
                            className="mdi mdi-chevron-up"
                            style={{
                                fontSize: '1.5rem',
                                marginRight: '0.5rem',
                                transition: 'transform 0.2s',
                                transform: showAll ? 'rotate(180deg)' : 'rotate(90deg)'
                            }}
                        />
                        Advanced Configuration
                    </div>
                    <Collapse in={showAll}>
                        <div className="mt-3 ps-5 pe-5">
                            
                                <h4>Build Credentials</h4>
                                <p className="text-muted">These credentials are used for building your models and running queries. It's best practice to keep these separate from your loader credentials.</p>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    
                                    <Form.Control
                                        name="username"
                                        type="text"
                                        placeholder=""
                                        value={snowflakeDetails.username}

                                        onChange={(e) => onChange('username', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Private Key</Form.Label>
                                    <Form.Control as="textarea" value={snowflakeDetails.private_key} onChange={(e) => onChange('private_key', e.target.value)}/>
                                    <Form.Text>Pliable will automatically generate a keypair if this is left empty. If you'd like to do it yourself, please paste the entire private key here, including "-----BEGIN PRIVATE KEY-----" and "-----END PRIVATE KEY-----". For more information about using key-pair auth with Snowflake, please visit the <a target="_blank" href="https://docs.snowflake.com/en/user-guide/key-pair-auth">Snowflake documentation</a>.</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Private Key Passphrase (Optional)</Form.Label>
                                    <Form.Control
                                        name="private_key_passphrase"
                                        type="password"
                                        placeholder=""
                                        value={snowflakeDetails.private_key_passphrase}

                                        onChange={(e) => onChange('private_key_passphrase', e.target.value)}
                                    />
                                </Form.Group>
                                
                            
                                <Form.Group className="mb-3">
                                    <Form.Label>Database</Form.Label>
                                    <div></div>
                                    <Form.Control
                                        name="database"
                                        type="text"
                                        placeholder=""
                                        value={snowflakeDetails.database}

                                        onChange={(e) => onChange('database', e.target.value)}
                                    />
                                    <Form.Text>
                                        Enter the name of the database where Pliable should store its data
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Warehouse (Building)</Form.Label>
                                    
                                    <Form.Control
                                        name="build_warehouse"
                                        type="text"
                                        placeholder=""
                                        value={snowflakeDetails.build_warehouse}

                                        onChange={(e) => onChange('build_warehouse', e.target.value)}
                                    />
                                    <Form.Text>
                                        Enter the name of the warehouse Pliable should use for building your models.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Warehouse (Querying)</Form.Label>
                                    <Form.Control
                                        name="query_warehouse"
                                        type="text"
                                        placeholder=""
                                        value={snowflakeDetails.query_warehouse}

                                        onChange={(e) => onChange('query_warehouse', e.target.value)}
                                    />
                                    <Form.Text>
                                        Enter the name of the warehouse Pliable should use for querying your models. It's best practice to keep these separate so you don't compete for resources with the scheduled build processes.
                                    </Form.Text>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Control
                                        name="role"
                                        type="text"
                                        placeholder=""
                                        value={snowflakeDetails.role}

                                        onChange={(e) => onChange('role', e.target.value)}
                                    />
                                    <Form.Text>
                                        Enter the name of the role Pliable should use when interacting with Snowflake. 
                                    </Form.Text>
                                </Form.Group>
                                <hr />
                            <h4>Loading Credentials (Optional)</h4>
                            <p className="text-muted">If you are using Pliable's managed ETL functionality, you will also need to provide credentials for loading data into Snowflake.</p>
                            <Form.Group className="mb-3">
                                <Form.Label>User</Form.Label>
                                <Form.Control
                                    name="loader_user"
                                    type="text"
                                    placeholder=""
                                    value={snowflakeDetails.loader_username}

                                    onChange={(e) => onChange('loader_username', e.target.value)}
                                />
                                <Form.Text>
                                    Enter a username to use for loading data into your Snowflake
                                </Form.Text>
                            </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Private Key</Form.Label>
                                        <Form.Control as="textarea" value={snowflakeDetails.loader_private_key} onChange={(e) => onChange('loader_private_key', e.target.value)}/>
                                        <Form.Text>Paste the entire private key here, including "-----BEGIN PRIVATE KEY-----" and "-----END PRIVATE KEY-----". For more information about using key-pair auth with Snowflake, please visit the <a target="_blank" href="https://docs.snowflake.com/en/user-guide/key-pair-auth">Snowflake documentation</a>.</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Private Key Passphrase (Optional)</Form.Label>
                                        <Form.Control
                                            name="loader_private_key_passphrase"
                                            type="password"
                                            placeholder=""
                                            value={snowflakeDetails.loader_private_key_passphrase}

                                            onChange={(e) => onChange('loader_private_key_passphrase', e.target.value)}
                                        />
                                    </Form.Group>
                                
                                
                            <Form.Group className="mb-3">
                                <Form.Label>Database</Form.Label>
                                <Form.Control
                                    name="loader_database"
                                    type="text"
                                    placeholder=""
                                    value={snowflakeDetails.loader_database}

                                    onChange={(e) => onChange('loader_database', e.target.value)}
                                />
                                <Form.Text>
                                    Enter the name of the database for housing raw data ingested from your data sources.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Role</Form.Label>
                                <Form.Control
                                    name="loader_role"
                                    type="text"
                                    placeholder=""
                                    value={snowflakeDetails.loader_role}

                                    onChange={(e) => onChange('loader_role', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Warehouse (Loading)</Form.Label>
                                <Form.Control
                                    name="loading_warehouse"
                                    type="text"
                                    placeholder=""
                                    value={snowflakeDetails.loading_warehouse}

                                    onChange={(e) => onChange('loading_warehouse', e.target.value)}
                                />
                                <Form.Text>
                                    Enter the name of the warehouse Pliable should use for loading in data.
                                </Form.Text>
                            </Form.Group>

                        </div>
                    </Collapse>
                </div>
            </div>
            
            
            
        </>}
        {step == 'SCRIPT' && <>
            <h3 className="mb-0">Create Snowflake Resources</h3>
            <p className="text-muted">We'll generate a setup script that will create all of the resources Pliable needs to operate in your Snowflake account. Once you run the script, click on "Validate Connection" to make sure everything is working.</p>     
            <AsyncButton
                variant="primary"
                icon='mdi mdi-code-braces'
                className="me-2"
                disabled={!formIsValid}
                loading={savingConfig}
                onClick={() => saveSnowflakeDetails(true)}
                text="Generate Setup Script"
            />

            
            <AsyncButton
                variant="pliable"
                icon='mdi mdi-database-check'
                loading={testingCredentials}
                onClick={() => testCredentials()}
                text="Validate Connection"
            />

            {(testingCredentials || credentialsError || credentialsWarning || credsAreValid) && <div className="mt-3">

                <div className="card">
                    <div className="card-body">
                        {testingCredentials && (
                            <div>
                                <Form.Text>
                                    We're running a few different checks, which may take up to 30 seconds.
                                </Form.Text>
                            </div>
                            
                        )}
                        {credentialsError && (
                            <>
                                <p>
                                    <strong className="text-danger">Oops!</strong> We were unable to connect to your Snowflake. Please check your credentials and make sure the setup script ran successfully.
                                </p>
                                <Danger>
                                    {credentialsError}
                                </Danger>
                            </>
                        )}
                        {credentialsWarning && (
                            <div className="mt-2">
                                <Warning><div>{credentialsWarning}</div></Warning>
                            </div>
                        )}
                        {!testingCredentials && credsAreValid && <div className="mt-2">
                            <SuccessAlert>
                                <div className="flex-1">Connection successful!</div>
                                
                            </SuccessAlert>

                        </div>} 
                    </div>

                </div>
            </div>}
            
        </>}
            
        
                    
    </div>
}

export default SnowflakeSetupForm;